import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const LEVELS = ["Nursery", "Primary", "Secondary", "Sixth Form"];
const DISTRICTS = [
  "Maitama", "Asokoro", "Wuse 2", "Wuse", "Gwarinpa", "Jabi", "Jahi",
  "Durumi", "Lugbe", "Karu", "Kuje", "Life Camp", "Garki", "Utako",
  "Lokogoma", "Dawaki", "Apo", "Guzape", "Katampe", "Gudu", "Kubwa",
  "Mabushi", "Kaura", "Mararaba", "Dakwo", "Kwali", "Gwagwalada", "Bwari",
];

const FACILITY_SUGGESTIONS = [
  "Library", "Computer Lab", "Science Lab", "Sports Field", "Music Room",
  "Art Studio", "Clinic / Sick Bay", "School Bus", "Boarding Facilities",
  "Swimming Pool", "Playground", "ICT Lab", "Assembly Hall",
];

const empty = {
  name: "",
  type: "Private",
  levels: [],
  district: "",
  address: "",
  phone: "",
  email: "",
  website: "",
  year_established: "",
  fee_min: "",
  fee_max: "",
  facilities: [],
  image_url: "",
  description: "",
};

export default function Submit() {
  const [form, setForm] = useState(empty);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArray = (key, value) => {
    setForm((f) => {
      const current = f[key] || [];
      const next = current.includes(value)
        ? current.filter((x) => x !== value)
        : [...current, value];
      return { ...f, [key]: next };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.district.trim() || form.levels.length === 0) {
      toast.error("Please fill in name, district and at least one level.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year_established: form.year_established
          ? Number(form.year_established)
          : null,
        fee_min: form.fee_min ? Number(form.fee_min) : null,
        fee_max: form.fee_max ? Number(form.fee_max) : null,
      };
      await api.post("/schools", payload);
      setSubmitted(true);
      toast.success("Submission received. Our team will review shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <div
          data-testid="submit-success"
          className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1E3A2B]/10 text-[#1E3A2B] mb-5"
        >
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h1 className="font-display text-3xl font-bold text-stone-900">
          Thank you — submission received
        </h1>
        <p className="mt-3 text-stone-600 leading-relaxed">
          Your school has been added to our review queue. Our team typically
          responds within 3 business days. If approved, your listing will
          appear in the directory.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <button
            onClick={() => {
              setForm(empty);
              setSubmitted(false);
            }}
            data-testid="submit-another-btn"
            className="bg-white border border-stone-200 hover:border-[#C85A32] text-stone-900 font-semibold px-5 py-2.5 rounded-xl"
          >
            Submit another
          </button>
          <button
            onClick={() => navigate("/browse")}
            className="bg-[#C85A32] hover:bg-[#A84624] text-white font-semibold px-5 py-2.5 rounded-xl"
          >
            Browse schools
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C85A32]">
        For schools
      </div>
      <h1
        className="font-display text-3xl md:text-4xl font-bold text-stone-900 mt-2"
        data-testid="submit-title"
      >
        Submit your school
      </h1>
      <p className="mt-3 text-stone-600 leading-relaxed">
        Add your institution to Best Schools in Abuja. Submissions are reviewed
        by our team before being published.
      </p>

      <form
        onSubmit={onSubmit}
        className="mt-10 bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 space-y-6"
      >
        <Section title="Basic information">
          <Field label="School name *">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              data-testid="submit-input-name"
              required
              className="input"
              placeholder="e.g. Sunrise Preparatory School"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Type *">
              <select
                value={form.type}
                onChange={(e) => setField("type", e.target.value)}
                data-testid="submit-input-type"
                className="input"
              >
                <option value="Private">Private</option>
                <option value="Public">Public</option>
              </select>
            </Field>
            <Field label="District *">
              <select
                value={form.district}
                onChange={(e) => setField("district", e.target.value)}
                data-testid="submit-input-district"
                required
                className="input"
              >
                <option value="">Select a district</option>
                {DISTRICTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Levels offered *">
            <div className="flex flex-wrap gap-2">
              {LEVELS.map((lvl) => (
                <button
                  type="button"
                  key={lvl}
                  data-testid={`submit-level-${lvl.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => toggleArray("levels", lvl)}
                  className={`text-sm font-medium px-3 py-1.5 rounded-full border transition-colors ${
                    form.levels.includes(lvl)
                      ? "bg-[#C85A32] text-white border-[#C85A32]"
                      : "bg-white text-stone-700 border-stone-200 hover:border-[#C85A32]"
                  }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          </Field>
        </Section>

        <Section title="Location & contact">
          <Field label="Address">
            <input
              type="text"
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
              data-testid="submit-input-address"
              className="input"
              placeholder="Street, estate, Abuja"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Phone">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                data-testid="submit-input-phone"
                className="input"
                placeholder="+234 ..."
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                data-testid="submit-input-email"
                className="input"
                placeholder="contact@yourschool.ng"
              />
            </Field>
          </div>
          <Field label="Website">
            <input
              type="url"
              value={form.website}
              onChange={(e) => setField("website", e.target.value)}
              data-testid="submit-input-website"
              className="input"
              placeholder="https://yourschool.ng"
            />
          </Field>
        </Section>

        <Section title="Details">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Year established">
              <input
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                value={form.year_established}
                onChange={(e) => setField("year_established", e.target.value)}
                data-testid="submit-input-year"
                className="input"
              />
            </Field>
            <Field label="Fees from (₦/year)">
              <input
                type="number"
                min="0"
                value={form.fee_min}
                onChange={(e) => setField("fee_min", e.target.value)}
                data-testid="submit-input-feemin"
                className="input"
                placeholder="e.g. 500000"
              />
            </Field>
            <Field label="Fees to (₦/year)">
              <input
                type="number"
                min="0"
                value={form.fee_max}
                onChange={(e) => setField("fee_max", e.target.value)}
                data-testid="submit-input-feemax"
                className="input"
                placeholder="e.g. 1500000"
              />
            </Field>
          </div>
          <Field label="Facilities">
            <div className="flex flex-wrap gap-2">
              {FACILITY_SUGGESTIONS.map((f) => (
                <button
                  type="button"
                  key={f}
                  data-testid={`submit-facility-${f.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  onClick={() => toggleArray("facilities", f)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                    form.facilities.includes(f)
                      ? "bg-[#1E3A2B] text-white border-[#1E3A2B]"
                      : "bg-white text-stone-700 border-stone-200 hover:border-[#1E3A2B]"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Logo / campus image URL">
            <input
              type="url"
              value={form.image_url}
              onChange={(e) => setField("image_url", e.target.value)}
              data-testid="submit-input-image"
              className="input"
              placeholder="https://..."
            />
          </Field>
          <Field label="Description">
            <textarea
              rows={5}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              data-testid="submit-input-description"
              className="input resize-y"
              placeholder="Describe your school's approach, strengths and what makes it unique."
            />
          </Field>
        </Section>

        <div className="pt-2 flex items-center justify-end gap-3 border-t border-stone-100">
          <button
            type="submit"
            disabled={saving}
            data-testid="submit-submit-btn"
            className="bg-[#C85A32] hover:bg-[#A84624] disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            {saving ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </form>

      <style>{`.input{width:100%;background:#faf8f4;border:1px solid #E5E0D8;border-radius:0.5rem;padding:0.6rem 0.75rem;font-size:0.925rem;color:#1C1917;outline:none;transition:border-color .15s}.input:focus{border-color:#C85A32;background:#fff}`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-4">
      <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 pb-2 border-b border-stone-100">
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-stone-700 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
